import React, { useMemo } from 'react';
import { StyleSheet, View, Pressable, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import useResponsiveness from '@/hooks/useResponsiveness';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';

const PURPLE = '#7C3AED';

function CreditPackCard({
  credits,
  price,
  name,
  isBestValue,
  isPopular,
  isSelected,
  onSelect,
  savingsPercent,
  priceLabel,
  disabled,
}) {
  const { colors, theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const { isXS } = useResponsiveness();
  const styles = useMemo(() => createStyles(colors, isDark, isXS), [colors, isDark, isXS]);

  const hasTag = isBestValue || isPopular;
  const tagLabel = isBestValue ? 'Best Value' : isPopular ? 'Popular' : null;
  const tagStyle = isBestValue ? styles.bestValueTag : isPopular ? styles.popularTag : null;

  return (
    <Pressable
      onPress={onSelect}
      disabled={disabled}
      style={({ hovered }) => [
        styles.card,
        isSelected && styles.cardSelected,
        hovered && !isSelected && !disabled && styles.cardHovered,
        disabled && styles.cardDisabled,
      ]}
    >
      {hasTag && (
        <View style={[styles.tag, tagStyle]}>
          <Text size="xs" weight="bold" style={styles.tagText}>
            {tagLabel}
          </Text>
        </View>
      )}

      <View style={styles.row}>
        <View style={[styles.radio, isSelected && styles.radioSelected]}>
          {isSelected && <View style={styles.radioDot} />}
        </View>

        <View style={styles.creditInfo}>
          <View style={styles.creditRow}>
            <Icon name="coins" size={14} color={isSelected ? PURPLE : colors.text_color_primary} solid />
            <Text size="l" weight="bold" color="primary">
              {(credits ?? 0).toLocaleString()}
            </Text>
            <Text size="xs" color="secondary" style={styles.creditsLabel}>credits</Text>
          </View>
          {savingsPercent > 0 && (
            <View style={styles.metaRow}>
              <View style={styles.savingsBadge}>
                <Text size="xs" weight="bold" style={styles.savingsText}>
                  {savingsPercent}% off
                </Text>
              </View>
            </View>
          )}
        </View>

        <View style={styles.priceContainer}>
          <Text size="l" weight="bold" color="primary">{priceLabel}</Text>
          <Text size="xs" color="tertiary">one-time</Text>
        </View>
      </View>
    </Pressable>
  );
}

const createStyles = (colors, isDark, isXS) => StyleSheet.create({
  card: {
    position: 'relative',
    borderRadius: 14,
    borderWidth: 1.5,
    borderColor: colors.border_color_secondary,
    backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.015)',
    overflow: 'hidden',
    width: isXS ? '100%' : 'calc(33.333% - 8px)',
    minWidth: isXS ? undefined : 200,
    cursor: 'pointer',
    ...Platform.select({
      web: {
        transitionProperty: 'border-color, background-color, box-shadow',
        transitionDuration: '150ms',
      },
    }),
  },
  cardSelected: {
    borderColor: PURPLE,
    backgroundColor: PURPLE + '0A',
    ...Platform.select({
      web: {
        boxShadow: `0 0 0 1px ${PURPLE}40`,
      },
    }),
  },
  cardHovered: {
    borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)',
    backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.025)',
  },
  cardDisabled: {
    opacity: 0.5,
  },
  tag: {
    position: 'absolute',
    top: 0,
    right: 0,
    paddingHorizontal: 10,
    paddingVertical: 3,
    borderBottomLeftRadius: 8,
    zIndex: 1,
  },
  bestValueTag: {
    backgroundColor: '#10B981',
  },
  popularTag: {
    backgroundColor: PURPLE,
  },
  tagText: {
    color: '#fff',
    fontSize: 10,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: isXS ? 14 : 16,
    gap: 14,
  },
  radio: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 2,
    borderColor: colors.border_color_primary,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  radioSelected: {
    borderColor: PURPLE,
    borderWidth: 2,
  },
  radioDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: PURPLE,
  },
  creditInfo: {
    flex: 1,
    gap: 3,
  },
  creditRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  creditsLabel: {
    marginTop: 1,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  savingsBadge: {
    paddingHorizontal: 5,
    paddingVertical: 1,
    borderRadius: 4,
    backgroundColor: '#10B981' + '20',
  },
  savingsText: {
    color: '#10B981',
    fontSize: 10,
  },
  priceContainer: {
    alignItems: 'flex-end',
    flexShrink: 0,
  },
});

export default React.memo(CreditPackCard);
